import {ArrowBack} from '@mui/icons-material';
import {Alert,Box,Button,Card,CardContent,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,IconButton,Typography} from '@mui/material';
import React,{useEffect,useMemo,useState} from 'react';
import AudioPlayer from '../../../components/AudioPlayer';
import {apiFetch} from '../../../security/apiFetch';

interface AyahViewerProps {
  open: boolean;
  onClose: () => void;
  surah: number;
  ayah_start: number;
  ayah_end?: number;
  onBackToName: () => void;
  token?: string;
}

type RecordValue = Record<string,unknown>;
type Scope = {open:boolean;surah:number;ayah:number;token?:string;retry:number};
type TafsirBlock = {from:number;to:number;text:string};
type View = {scope:Scope;loading:boolean;error:string|null;text:string|null;source:string|null;
  english:string|null;audio:{url:string;reciter:string}|null;tafsir:TafsirBlock[]|null};

function object(value:unknown):RecordValue|null {
  return value!==null && typeof value==='object' && !Array.isArray(value) ? value as RecordValue : null;
}
function nonempty(value:unknown):value is string {return typeof value==='string' && value.trim().length>0;}
function sourceVerse(value:unknown,surah:number,ayah:number) {
  const root=object(value), verse=object(root?.verse), ordinals=object(verse?.ordinals);
  if(!root || !verse || verse.surah!==surah || ordinals?.kufan!==ayah || !nonempty(verse.text_uthmani)
      || !nonempty(verse.source_id) || !nonempty(root.corpus_version)) throw new Error('Invalid verse source');
  const translations=object(root.translations), english=object(translations?.['en-pickthall']);
  const enOrdinals=object(english?.ordinals);
  const englishText=english?.surah===surah && enOrdinals?.kufan===ayah && nonempty(english.text) ? english.text : null;
  return {text:verse.text_uthmani,source:`${verse.source_id} · ${root.corpus_version}`,english:englishText};
}
function sourceAudio(value:unknown,surah:number,ayah:number) {
  const data=object(value);
  if(!data || data.surah!==surah || data.ayah!==ayah || data.scope!=='ayah' || data.source!=='local_import'
      || !nonempty(data.recitation_id) || !/^[a-z0-9-]{1,80}$/.test(data.recitation_id)
      || !nonempty(data.reciter) || data.audio_url!==`/api/v1/quran/recitations/${data.recitation_id}/audio/${surah}/${ayah}`) return null;
  return {url:data.audio_url as string,reciter:data.reciter};
}
function sourceTafsir(value:unknown,surah:number,ayah:number):TafsirBlock[]|null {
  const data=object(value);
  if(!data || !nonempty(data.source_url) || !Array.isArray(data.blocks) || data.blocks.length===0) return null;
  const result:TafsirBlock[]=[];
  for(const item of data.blocks){
    const block=object(item),from=block?.from_aya,to=block?.to_aya;
    if(!block || block.sura!==surah || typeof from!=='number' || !Number.isInteger(from)
        || typeof to!=='number' || !Number.isInteger(to) || from>ayah || to<ayah || !nonempty(block.text)) return null;
    result.push({from,to,text:block.text});
  }
  return result;
}

const AyahViewer:React.FC<AyahViewerProps>=({open,onClose,surah,ayah_start,ayah_end,onBackToName,token})=>{
  const last=ayah_end??ayah_start;
  const rangeKey=`${surah}:${ayah_start}:${last}`;
  const [selection,setSelection]=useState({rangeKey,ayah:ayah_start});
  const ayah=selection.rangeKey===rangeKey?selection.ayah:ayah_start;
  const [retry,setRetry]=useState(0);
  const scope=useMemo<Scope>(()=>({open,surah,ayah,token,retry}),[open,surah,ayah,token,retry]);
  const [state,setState]=useState<View|null>(null);
  const view=state?.scope===scope?state:null;

  useEffect(()=>{
    if(!open)return;
    const initial:View={scope,loading:false,error:null,text:null,source:null,english:null,audio:null,tafsir:null};
    if(!Number.isInteger(surah)||surah<1||surah>114||!Number.isInteger(ayah_start)||ayah_start<1
        ||!Number.isInteger(last)||last<ayah_start||last>286||ayah<ayah_start||ayah>last){
      setState({...initial,error:'Некорректная ссылка на аят.'});return;
    }
    if(!token){setState({...initial,error:'Войдите, чтобы загрузить аят.'});return;}
    let active=true;
    const controller=new AbortController();
    const timer=window.setTimeout(()=>controller.abort(),15000);
    const headers={Authorization:`Bearer ${token}`};
    const get=async(path:string):Promise<unknown>=>{
      const response=await apiFetch(path,{headers,signal:controller.signal});
      if(!response.ok)throw new Error('Source unavailable');
      return response.json();
    };
    setState({...initial,loading:true});
    void (async()=>{
      try{
        const [verse,audio,tafsir]=await Promise.allSettled([
          get(`/api/v1/quran/reference/ayahs/${surah}/${ayah}`),
          get(`/api/v1/quran/audio/${surah}/${ayah}`),
          get(`/api/v1/quran/reference/tafsir/russian_saadi/${surah}/${ayah}`),
        ]);
        if(!active)return;
        if(verse.status!=='fulfilled')throw new Error('Verse unavailable');
        const real=sourceVerse(verse.value,surah,ayah);
        setState({...initial,...real,
          audio:audio.status==='fulfilled'?sourceAudio(audio.value,surah,ayah):null,
          tafsir:tafsir.status==='fulfilled'?sourceTafsir(tafsir.value,surah,ayah):null});
      }catch{
        if(active)setState({...initial,error:'Не удалось загрузить аят. Повторите попытку.'});
      }finally{window.clearTimeout(timer);}
    })();
    return()=>{active=false;window.clearTimeout(timer);controller.abort();};
  },[open,surah,ayah,ayah_start,last,token,scope]);

  const back=()=>{onBackToName();onClose();};
  return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{sx:{minHeight:'60vh'}}}>
    <DialogTitle><Box sx={{display:'flex',alignItems:'center',gap:2}}>
      <IconButton aria-label="Назад к имени" onClick={back} size="small"><ArrowBack/></IconButton>
      <Box><Typography variant="h6">Сура {surah}</Typography>
        <Typography variant="body2" color="text.secondary">Аят {ayah}{last!==ayah_start?` (ссылка: ${ayah_start}–${last})`:''}</Typography></Box>
    </Box></DialogTitle>
    <DialogContent>
      {(!view || view.loading) && <Box sx={{display:'flex',justifyContent:'center',py:4}}><CircularProgress/></Box>}
      {view?.error && <Alert severity="error" sx={{mb:2}}>{view.error} <Button onClick={()=>setRetry(value=>value+1)}>Повторить</Button></Alert>}
      {view?.text && <Box>
        <Card sx={{mb:3}}><CardContent>
          <Typography variant="h5" sx={{textAlign:'right',fontFamily:'Amiri, serif',fontSize:'1.8rem',lineHeight:2,mb:2}}>{view.text}</Typography>
          <Typography variant="caption">Источник: {view.source}</Typography>
          {view.audio ? <AudioPlayer src={view.audio.url} token={token} title={`Чтец: ${view.audio.reciter}`} sx={{mt:2}}/>
            : <Alert severity="warning" sx={{mt:2}}>Аудио этого аята недоступно.</Alert>}
        </CardContent></Card>
        <Card sx={{mb:3}}><CardContent><Typography variant="h6">Перевод</Typography>
          <Typography>Русский перевод этого аята недоступен.</Typography>
          {view.english && <><Typography variant="subtitle2">Английский перевод: Pickthall</Typography><Typography>{view.english}</Typography></>}
        </CardContent></Card>
        <Card><CardContent><Typography variant="h6">Тафсир Ас-Саади</Typography>
          {view.tafsir ? view.tafsir.map((block,index)=><Box key={`${block.from}:${block.to}:${index}`} sx={{mt:1}}>
            <Typography variant="caption">Аяты {block.from}–{block.to}</Typography>
            <Typography sx={{whiteSpace:'pre-wrap'}}>{block.text}</Typography>
          </Box>) : <Alert severity="warning">Тафсир для этого аята недоступен.</Alert>}
        </CardContent></Card>
      </Box>}
    </DialogContent>
    <DialogActions>
      {last>ayah_start && <><Button disabled={ayah<=ayah_start || !!view?.loading} onClick={()=>setSelection({rangeKey,ayah:ayah-1})}>Предыдущий аят</Button>
        <Button disabled={ayah>=last || !!view?.loading} onClick={()=>setSelection({rangeKey,ayah:ayah+1})}>Следующий аят</Button></>}
      <Button onClick={back} startIcon={<ArrowBack/>}>Назад к имени</Button>
      <Button onClick={onClose} variant="contained">Закрыть</Button>
    </DialogActions>
  </Dialog>;
};

export default AyahViewer;
